import { FormValues, VehicleTypeSelector, VehicleTypeRadioList } from '@topdanmark-onlinesolutions/claim-form-types';
import { memoizeOne } from '@flos/react-ui';
import { getAdditionalZones } from './mapping';

/**
 * Image paths list for vehicle types
 */
export const getImagePaths = memoizeOne(
    (contentBase: string) =>
        ({
            bus: `${contentBase}/images/vehicle-bus-200x420.png`,
            campingvgn: `${contentBase}/images/vehicle-campingvogn-200x420.png`,
            gaffeltruck: `${contentBase}/images/vehicle-gaffeltruck-200x420.png`,
            knallert: `${contentBase}/images/vehicle-knallert-200x420.png`,
            personbil: `${contentBase}/images/vehicle-personbil-200x420.png`,
            trailer: `${contentBase}/images/vehicle-trailer-200x420.png`,
            traktor: `${contentBase}/images/vehicle-traktor-200x420.png`,
            varevogn: `${contentBase}/images/vehicle-varevogn-200x420.png`,
            lastbil: `${contentBase}/images/vehicle-bus-200x420.png`
        } as const)
);

/**
 * Car damage options list
 */
export const getCarDamageAreaOptions = memoizeOne(() => [
    { value: 'front', label: 'Front', className: 'zone-BtnFront' },
    { value: 'hoejreFront', label: 'Højre front', className: 'zone-BtnRightFront' },
    { value: 'hoejreSide', label: 'Højre side', className: 'zone-BtnRight' },
    { value: 'hoejreBagende', label: 'Højre bagside', className: 'zone-BtnRightBack' },
    { value: 'bagende', label: 'Bagside', className: 'zone-BtnRear' },
    { value: 'venstreBagende', label: 'Venstre bagside', className: 'zone-BtnLeftBack' },
    { value: 'venstreSide', label: 'Venstre side', className: 'zone-BtnLeft' },
    { value: 'venstreFront', label: 'Venstre front', className: 'zone-BtnLeftFront' }
]);

/**
 * Get all avaiable zones (both carDamageAreas and addtional zones)
 */
export const getAllAvailableZones = () => {
    const damageAreaOptions = getCarDamageAreaOptions();
    const additionalZones = getAdditionalZones();

    // Car damage zone options
    const zoneOptions = damageAreaOptions.map((option) => {
        return { value: option.value, label: option.label };
    });

    // Addtional zone options
    const additionalZonesOptions = Object.keys(additionalZones).map((zone: string) => {
        const additionalZone = additionalZones[zone as keyof typeof additionalZones];
        return { value: additionalZone.value, label: additionalZone.label };
    });

    // Merge both to an array
    return zoneOptions.concat(additionalZonesOptions);
};

/**
 * Find vehicle type by selector
 */
export const getVehicleType = (formValues: FormValues, selector: VehicleTypeSelector | VehicleTypeRadioList) => {
    // The formValues might be empty while the state is beeing fecthed from the storage API if the calling component is used on a page that we deep link to
    if (Object.keys(formValues).length === 0) {
        return undefined;
    }

    /**
     * If user click on vehicle with policy in the selector the user will to direct to the damage zone selector
     * If user click on vehicle without policy the user should select which vehicle type on a radio button list element
     */

    switch (selector.type) {
        case 'vehicleTypeSelector':
            const { vehicleWithPolicy, vehicleWithoutPolicy } = selector;

            const valueWithPolicy = formValues[vehicleWithPolicy.vehicleSelectorElementId];

            if (!valueWithPolicy) {
                console.error(
                    'VehicleZones: Value field for known vehicle policy is empty! The vehicle should be selected before the component'
                );
            }
            const otherIsSelected = valueWithPolicy[vehicleWithPolicy.property] === vehicleWithPolicy.otherValue;
            if (!otherIsSelected) {
                // Map vehichle type from backend to known type
                // Only for vehicles with policy
                if (valueWithPolicy.entityType === 'motorcykel') {
                    valueWithPolicy.entityType = 'knallert';
                }

                if (valueWithPolicy.entityType === 'varelastbi') {
                    valueWithPolicy.entityType = 'varevogn';
                }
                return valueWithPolicy[vehicleWithPolicy.property];
            } else {
                // Should be used if the hasLicensPlate radio buttons with yes/no is defined
                const { vehicleTypeRadioListElementId } = vehicleWithoutPolicy;
                return formValues[vehicleTypeRadioListElementId]
                    ? formValues[vehicleTypeRadioListElementId].value
                    : undefined;
            }

        case 'vehicleTypeRadioList':
            if (formValues[selector.elementId] === undefined) {
                console.error(`VehicleZones: Selected elementId ${selector.elementId} is not found!`);
            } else {
                return formValues[selector.elementId].value;
            }
    }
};

export type VehicleType =
    | 'personbil'
    | 'knallert'
    | 'trailer'
    | 'campingvgn'
    | 'varevogn'
    | 'bus'
    | 'gaffeltruck'
    | 'traktor'
    | 'lastbil';
